import React, { ReactNode, useCallback, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import styles from "./Modal.module.css";

interface ModalState {
    isOpen: boolean;
    title?: string;
    children?: ReactNode;
    modalButtons?: ReactNode | (() => ReactNode);
    size?: "normal" | "wide";
    disableOverlayClose?: boolean;
    onCloseNavigatePath?: string | null; // 모달창 닫은 후 이동할 페이지 설정
    modalInput?: ReactNode | (() => ReactNode);
    onAfterClose?: (() => void) | null; //
}

interface ModalProps extends ModalState {
    onClose: () => void;
}

const Modal: React.FC<ModalProps> = ({ isOpen, title, children,modalInput, modalButtons, size = "normal", onClose, disableOverlayClose = false }) => {
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleOverlayClick = () => {
        if (!disableOverlayClose) {
            onClose();
        }
    };

    const renderedInput = typeof modalInput === "function" ? modalInput() : modalInput;
    const renderedButtons = typeof modalButtons === "function" ? modalButtons() : modalButtons;

    return (
        <div className={styles.modalOverlay} onClick={handleOverlayClick}>
            <div className={size === "wide" ? styles.modalContainerWide : styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                {/* 우측 상단 닫기 (X) 버튼 */}
                <div className={styles.modalCloseButton} onClick={onClose}>
                    &times;
                </div>

                {/* 모달 제목 */}
                {title && <h2 className={styles.modalTitle}>{title}</h2>}

                {/* 모달 컨텐츠 */}
                <div className={styles.modalBody}>{children}</div>
                {/* <div className={styles.modalInputWrapper}>{modalInput}</div> */}

                
                {modalInput && <div className={styles.modalButtons}>{renderedInput}</div>}

                {/* 하단 버튼 영역 */}
                {modalButtons && <div className={styles.modalButtons}>{renderedButtons}</div>}
            </div>
        </div>
    );
};

export type OpenModalParams = {
    title: string;
    children: ReactNode;
    modalButtons?: ReactNode | (() => ReactNode);
    size?: "normal" | "wide";
    onCloseNavigatePath?: string;
    disableOverlayClose?: boolean;
    modalInput?: ReactNode | (() => ReactNode);
    onAfterClose?: () => void;
};

export function useModal() {
    const [modalState, setModalState] = useState<ModalState>({
        isOpen: false,
        title: "",
        children: null,
        modalInput:null,
        modalButtons: null,
        size: "normal",
        disableOverlayClose: false,
        onCloseNavigatePath: null,
        onAfterClose: null,
    });

    const navigate = useNavigate();
    // 모달 닫힌 이후 동작해주기 위한 참조변수
    const prevIsOpen = useRef(modalState.isOpen);

    // const [onCloseRedirectPath, setOnCloseRedirectPath] = useState<string | null>(null);


    // 모달 열기 함수
    const openModal = useCallback(({ title, children, modalInput= null, modalButtons = null, size = "normal", onCloseNavigatePath, disableOverlayClose = false ,onAfterClose}: OpenModalParams) => {
        setModalState({
            isOpen: true,
            title,
            children,
            modalInput,
            modalButtons,
            size,
            disableOverlayClose,
            onCloseNavigatePath: onCloseNavigatePath ?? null,
            onAfterClose: onAfterClose ?? null,
        });
        // setOnCloseRedirectPath(onCloseNavigatePath ?? null);
    }, []);

    // 모달 닫기 함수
    const closeModal = useCallback(() => {
        setModalState((prev) => {
            const next = { ...prev, isOpen: false };

            if (prev.onAfterClose) {
                prev.onAfterClose();
            }

            return next;
        });
    }, []);

    // useEffect
    useEffect(() => {
        // 열려 있던 (isOpen=true)상태에서 -> 닫힘(isOpen=false)로 바뀐 경우
        if (prevIsOpen.current === true && modalState.isOpen === false) {
            const path = modalState.onCloseNavigatePath;
            if (path) {
                navigate(path);
            }
        }
        prevIsOpen.current = modalState.isOpen;
    }, [modalState.isOpen, modalState.onCloseNavigatePath, navigate]);

    // Modal 컴포넌트 반환
    const ModalComponent = useCallback(
        () => (
            <Modal
                isOpen={modalState.isOpen}
                title={modalState.title}
                children={modalState.children}
                modalButtons={modalState.modalButtons}
                modalInput={modalState.modalInput}
                size={modalState.size}
                onClose={closeModal}
                disableOverlayClose={modalState.disableOverlayClose}
            />
        ),
        [modalState, closeModal]
    );

    return { ModalComponent, openModal, closeModal };
}
